import { CarouselModal } from '@/components/common/carousel-modal';
import { ModalButton } from '@/components/common/modal-button';
import { FacilityCategoryIcon } from '@/components/feature/facility/facility-category-icon';
import { MarkerIcon } from '@/components/icons';
import { useOrientation } from '@/hooks';
import { useConfigStore } from '@/stores';
import type { FacilityCategoryItem } from '@/types/facility-category';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface FacilityCategoryDetailModalProps {
    isOpen: boolean;
    category: FacilityCategoryItem | null;
    onClose: () => void;
    onLocationCheck: () => void;
}

const FacilityCategoryDetailModal = ({ isOpen, category, onClose, onLocationCheck }: FacilityCategoryDetailModalProps) => {
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();

    const isFullscreenHorizontal = screenMode === 'fullscreen' && isHorizontal;
    const isHalfmodeHorizontal = screenMode === 'split' && isHorizontal;
    const isHalfmodeVertical = screenMode === 'split' && !isHorizontal;
    const isPositionedHorizontal = isFullscreenHorizontal || isHalfmodeHorizontal;

    const wrapperClassName = useMemo(
        () =>
            clsx({
                [styles['wrapper--positioned-horizontal']]: isPositionedHorizontal,
                [styles['wrapper--halfmode-vertical']]: isHalfmodeVertical,
            }),
        [isPositionedHorizontal, isHalfmodeVertical]
    );

    const bodyWrapClassName = useMemo(
        () =>
            clsx(styles.bodyWrap, {
                [styles['bodyWrap--positioned-horizontal']]: isPositionedHorizontal,
                [styles['bodyWrap--fullscreen-horizontal']]: isFullscreenHorizontal,
                [styles['bodyWrap--halfmode-horizontal']]: isHalfmodeHorizontal,
                [styles['bodyWrap--halfmode-vertical']]: isHalfmodeVertical,
            }),
        [isPositionedHorizontal, isFullscreenHorizontal, isHalfmodeHorizontal, isHalfmodeVertical]
    );

    if (!isOpen || !category) {
        return null;
    }

    return (
        <CarouselModal
            isOpen={isOpen}
            onClose={onClose}
            closePlacement="below-right"
            wrapperClassName={wrapperClassName}
            bodyWrapClassName={bodyWrapClassName}
            closeIconTabIndex={0}
            closeIconRegionOrder={4}
            contentClassName={clsx(styles.modal, {
                [styles['modal__contrast']]: contrast,
            })}
        >
            <section className={styles.titleSection}>
                <h1
                    data-modal-region
                    data-modal-region-order={1}
                    tabIndex={0}
                    data-interactive
                    aria-label={category.name}
                    className={clsx(styles.title, 'focus-border')}
                >
                    {category.name}
                </h1>
            </section>

            <section className={styles.iconSection} aria-hidden="true">
                <div className={styles.icon}>
                    <FacilityCategoryIcon
                        clientCode={category.clientCode}
                        contrast={contrast}
                        isHalfMode={screenMode === 'split'}
                        variant="modal"
                    />
                </div>
            </section>

            <section className={styles.guideSection}>
                <div className={styles.guideBox}>
                    <p
                        className={clsx(styles.guideText, 'focus-border')}
                        data-modal-region
                        data-modal-region-order={2}
                        tabIndex={0}
                        data-interactive
                        aria-label={t('facility.category.guide_text')}
                    >
                        {t('facility.category.guide_text')}
                    </p>
                </div>
            </section>

            <section className={styles.buttonSection}>
                <ModalButton
                    className={styles.locationButton}
                    data-modal-region
                    data-modal-region-order={3}
                    data-interactive
                    aria-label={t('facility.modal.tts.buttons.location_check')}
                    variant="secondary"
                    icon={<MarkerIcon width={28} height={28} />}
                    size="fit"
                    tabIndex={0}
                    onClick={onLocationCheck}
                    role="button"
                    data-role={t('common.data_role.button')}
                >
                    {t('facility.modal.text.buttons.location_check')}
                </ModalButton>
            </section>
        </CarouselModal>
    );
};

export default FacilityCategoryDetailModal;
